import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon, ShieldCheckIcon, BoltIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import PublicFooter from '../../components/common/PublicFooter';
import { SUPPORT_CONTACT } from '../../utils/constants';

const schema = yup.object().shape({
  identifier: yup
    .string()
    .required('Email or username is required')
    .min(3, 'Must be at least 3 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Must be at least 6 characters'),
});

interface LoginFormData {
  identifier: string;
  password: string;
}

const BRAND_FEATURES = [
  { icon: BoltIcon,           text: 'Instant delivery across all networks' },
  { icon: CurrencyDollarIcon, text: 'Wholesale prices for agents & resellers' },
  { icon: ShieldCheckIcon,    text: 'Secure payments via Paystack & MoMo' },
];

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (isAuthenticated) {
      const params = new URLSearchParams(location.search);
      const redirectTo = params.get('redirect') || '/dashboard';
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, location.search]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      setIsSubmitting(true);
      await login(data.identifier, data.password);
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Login failed';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left brand panel (desktop only) ── */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between p-10 xl:p-14 relative overflow-hidden bg-gradient-to-br from-navy-900 via-navy-800 to-navy-950">
        {/* Subtle green radial glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary-500/8 rounded-full blur-2xl translate-y-1/3 -translate-x-1/3" />
        </div>

        {/* Top: logo mark */}
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-brand">
              <img src="/logo-dark.svg" alt="GH Data Market" className="w-7 h-7" draggable={false} />
            </div>
            <div className="leading-none">
              <p className="text-white font-bold text-lg tracking-tight">
                <span className="text-white">GH</span><span className="text-primary-400">Data</span>
              </p>
              <p className="text-navy-300 text-xs font-medium tracking-widest uppercase">Market</p>
            </div>
          </div>
        </div>

        {/* Centre: headline + features */}
        <div className="relative space-y-8">
          <div>
            <p className="text-primary-400 text-sm font-semibold tracking-widest uppercase mb-3">
              Ghana's #1 Data Marketplace
            </p>
            <h2 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight">
              Fast.<br />
              Affordable.<br />
              <span className="text-primary-400">Always On.</span>
            </h2>
          </div>

          <ul className="space-y-4">
            {BRAND_FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-500/15 border border-primary-500/25 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-primary-400" />
                </span>
                <span className="text-navy-200 text-sm font-medium">{text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom: tagline */}
        <div className="relative">
          <p className="text-navy-400 text-xs">
            © {new Date().getFullYear()} GH Data Market · Built for Ghana
          </p>
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center bg-[#f8faf9] dark:bg-navy-900 px-4 sm:px-6 py-8 safe-area-top">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-[420px] space-y-6"
        >
          {/* Mobile logo — hidden on desktop where the left panel shows it */}
          <div className="lg:hidden text-center space-y-1">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center shadow-brand">
                <img src="/logo-dark.svg" alt="GH Data Market" className="w-9 h-9" draggable={false} />
              </div>
            </div>
            <h1 className="text-2xl font-extrabold text-navy-900 dark:text-white tracking-tight">
              <span className="text-navy-900 dark:text-white">GH</span><span className="text-primary-500">Data</span>{' '}
              <span className="text-navy-900 dark:text-white">Market</span>
            </h1>
            <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase">
              Fast · Affordable · Always On
            </p>
          </div>

          {/* Heading */}
          <div className="lg:pt-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-navy-900 dark:text-white">
              Welcome back
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-navy-300">
              Sign in to your account to continue
            </p>
          </div>

          {/* Form card */}
          <div className="bg-white dark:bg-navy-800 rounded-2xl shadow-medium border border-gray-100 dark:border-navy-700 p-6 sm:p-8 space-y-5">
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label htmlFor="identifier" className="form-label">
                  Email or Username
                </label>
                <Input
                  id="identifier"
                  type="text"
                  placeholder="Enter your email or username"
                  error={errors.identifier?.message}
                  {...register('identifier')}
                  disabled={isSubmitting}
                  className="form-input"
                  autoComplete="username"
                />
              </div>

              <div className="form-group">
                <div className="flex items-center justify-between mb-1">
                  <label htmlFor="password" className="form-label mb-0">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400 transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    error={errors.password?.message}
                    rightIcon={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="text-gray-400 hover:text-gray-500 dark:hover:text-navy-200 transition-colors p-1 rounded-md hover:bg-gray-100 dark:hover:bg-navy-700 touch-target"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    }
                    {...register('password')}
                    disabled={isSubmitting}
                    className="form-input pr-12"
                    autoComplete="current-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="lg"
                loading={isSubmitting || loading}
                disabled={isSubmitting || loading}
                className="w-full font-semibold"
              >
                {isSubmitting || loading ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-navy-700" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white dark:bg-navy-800 text-gray-400">
                  New to GH Data Market?
                </span>
              </div>
            </div>

            <Link
              to="/register"
              className="flex items-center justify-center w-full px-4 py-3 rounded-xl border-2 border-primary-500/40 text-primary-600 dark:text-primary-400 font-semibold text-sm hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all duration-200"
            >
              Create a free account
            </Link>
          </div>

          {/* Help row */}
          <p className="text-center text-xs text-gray-400 dark:text-navy-400">
            Need help?{' '}
            <a href={`mailto:${SUPPORT_CONTACT.EMAIL}`} className="text-primary-600 dark:text-primary-400 hover:underline">
              Contact support
            </a>
          </p>
        </motion.div>
        <PublicFooter />
      </div>
    </div>
  );
};

export default LoginPage;
